import React, { useState, useEffect, useRef } from "react";

const DEFAULT_PROMPT   = "Take a moment to write down what you're feeling right now.";
const DEFAULT_DURATION = 120;

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function JournalingTemplate({ config = {}, onProgress, onComplete }) {
  const prompt   = config.prompt   || DEFAULT_PROMPT;
  const duration = config.duration || DEFAULT_DURATION;

  const [text, setText]         = useState("");
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isActive, setIsActive] = useState(false);
  const textareaRef             = useRef(null);
  const completedRef            = useRef(false);

  // Auto-start timer when user begins typing
  const handleTextChange = (e) => {
    setText(e.target.value);
    if (!isActive && timeLeft === duration) {
      setIsActive(true);
    }
  };

  useEffect(() => {
    if (!isActive || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          setIsActive(false);
          if (!completedRef.current) {
            completedRef.current = true;
            onComplete?.();
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isActive, timeLeft]); // eslint-disable-line

  // Progress: fraction of time elapsed
  useEffect(() => {
    const elapsed  = duration - timeLeft;
    const progress = Math.min(elapsed / duration, 1);
    const label    = timeLeft > 0 ? `${formatTime(timeLeft)} remaining` : "Time's up";
    onProgress?.(progress, label);
  }, [timeLeft]); // eslint-disable-line

  const charCount = text.length;

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-w-full tw-gap-4">
      {/* Timer badge */}
      <div
        className="tw-rounded-2xl tw-px-4 tw-py-1.5 tw-text-base tw-font-semibold tw-tabular-nums"
        style={{
          background: "rgba(203,90,60,0.12)",
          border: "1px solid rgba(203,90,60,0.3)",
          color: "#CB5A3C",
        }}
      >
        {formatTime(timeLeft)}
      </div>

      {/* Prompt */}
      <p
        className="tw-text-xl tw-font-normal tw-text-center tw-max-w-[340px] tw-leading-relaxed"
        style={{ color: "rgba(255,255,255,0.85)" }}
      >
        {prompt}
      </p>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={text}
        onChange={handleTextChange}
        placeholder="Start writing — the timer begins when you do..."
        disabled={timeLeft <= 0}
        className="tw-w-full tw-rounded-2xl tw-text-base tw-leading-relaxed tw-resize-none tw-outline-none tw-transition-all tw-duration-300"
        style={{
          height: 160,
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.12)",
          padding: "16px",
          color: "rgba(255,255,255,0.85)",
          fontFamily: "inherit",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "rgba(160,140,255,0.6)";
          e.target.style.boxShadow   = "0 0 0 3px rgba(91,63,214,0.2)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "rgba(255,255,255,0.12)";
          e.target.style.boxShadow   = "none";
        }}
      />

      {/* Footer: char count + manual start button */}
      <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
        <span className="tw-text-sm" style={{ color: "rgba(255,255,255,0.35)" }}>
          {charCount} chars
        </span>
        {!isActive && timeLeft === duration && (
          <button
            className="tw-text-sm tw-font-semibold tw-px-4 tw-py-1.5 tw-rounded-2xl tw-transition-all"
            style={{
              border: "1px solid rgba(160,140,255,0.4)",
              background: "rgba(91,63,214,0.15)",
              color: "rgba(160,140,255,0.9)",
            }}
            onClick={() => {
              setIsActive(true);
              textareaRef.current?.focus();
            }}
          >
            Start timer
          </button>
        )}
      </div>
    </div>
  );
}
